import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import type { AuditLogFilters } from '@/entities/tenant';
import { currentUser, errorMessage } from '@/shared/api';
import { Pager } from '@/shared/ui';

import { paths } from '@/app/router/paths';
import { AdminHeader } from './components/AdminHeader';
import { AdminRegion } from './components/AdminRegion';
import { AuditLogCard } from './components/AuditLogCard';
import { CreditBookingForm } from './components/CreditBookingForm';
import { CreditRulesCard } from './components/CreditRulesCard';
import { ImpersonationForm } from './components/ImpersonationForm';
import { ImpersonationSessionsCard } from './components/ImpersonationSessionsCard';
import { TenantDetail } from './components/TenantDetail';
import { TenantMembersCard } from './components/TenantMembersCard';
import { adminCopy } from './copy';
import {
    useAuditLogs,
    useBookCredits,
    useChangeCreditRules,
    useChangePlan,
    useEndImpersonation,
    usePlans,
    usePlatformTenant,
    usePlatformUsers,
    useResumeTenant,
    useStartImpersonation,
    useSuspendTenant,
    useTenantImpersonations,
} from './hooks';

/** Vanuit het detail van een persoon: wie er al gekozen staat om mee te kijken. */
const USER_PARAM = 'gebruiker';

/**
 * Platformbeheer - één organisatie, op haar uuid (`/admin/organisaties/{uuid}`).
 *
 * Links wat de organisatie is en wie er werkt, met haar audit log; rechts wat
 * het platform ermee kan: de tegoedregels zetten, tegoed boeken en meekijken. Wat
 * het platform doet, komt met naam in die audit log; boeken en meekijken vragen
 * ook een reden.
 */
export function AdminTenantPage() {
    const { tenantId = '' } = useParams<{ tenantId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [auditFilters, setAuditFilters] = useState<AuditLogFilters>({ page: 1 });

    const tenant = usePlatformTenant(tenantId);
    const plans = usePlans(tenantId);
    const members = usePlatformUsers({ tenantUuid: tenantId, perPage: 100 }, tenantId !== '');
    const sessions = useTenantImpersonations(tenantId);
    const audit = useAuditLogs({ ...auditFilters, tenantUuid: tenantId });

    const suspend = useSuspendTenant(tenantId);
    const resume = useResumeTenant(tenantId);
    const changePlan = useChangePlan(tenantId);
    const book = useBookCredits(tenantId);
    const creditRules = useChangeCreditRules(tenantId);
    const impersonate = useStartImpersonation({
        uuid: tenantId,
        name: tenant.data?.name ?? '',
    });
    const endSession = useEndImpersonation(tenantId);

    const saving = suspend.isPending || resume.isPending || changePlan.isPending;
    const tenantError = suspend.error ?? resume.error ?? changePlan.error;
    const changedPlan = changePlan.isSuccess ? changePlan.data.planName : null;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <AdminHeader
                back={{ to: paths.adminTenants, label: adminCopy.tenantBack }}
                eyebrow={`${adminCopy.eyebrowPlatform} · ${adminCopy.tenantEyebrow(tenant.data?.slug ?? tenantId)}`}
                title={tenant.data?.name ?? adminCopy.tenantsTitle}
            />

            <div className="flex-1 overflow-auto bg-[var(--surface-page)] px-7 pt-[22px] pb-8">
                <AdminRegion
                    title={adminCopy.tenantLoading}
                    errorTitle={adminCopy.tenantError}
                    query={tenant}
                    skeletonHeight={220}
                >
                    {tenant.data ? (
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-start gap-[14px]">
                            <div className="flex min-w-0 flex-col gap-[14px]">
                                <TenantDetail
                                    tenant={tenant.data}
                                    plans={plans.data ?? []}
                                    plansLoading={plans.isPending}
                                    saving={saving}
                                    notice={changedPlan ? adminCopy.planSaved(changedPlan) : null}
                                    error={tenantError ? errorMessage(tenantError) : null}
                                    onChangePlan={(planCode) => changePlan.mutate(planCode)}
                                    onSuspend={(reason) => suspend.mutate(reason)}
                                    onResume={() => resume.mutate()}
                                />

                                <AdminRegion title={adminCopy.membersTitle} query={members}>
                                    {members.data ? (
                                        <TenantMembersCard
                                            tenantUuid={tenant.data.uuid}
                                            tenantName={tenant.data.name}
                                            users={members.data.items}
                                        />
                                    ) : null}
                                </AdminRegion>

                                <AdminRegion title={adminCopy.auditTitle} query={audit}>
                                    {audit.data ? (
                                        <AuditLogCard
                                            entries={audit.data.items}
                                            filters={auditFilters}
                                            onFiltersChange={setAuditFilters}
                                            tenants={[tenant.data]}
                                            lockedTenant
                                            footer={
                                                <Pager
                                                    meta={audit.data.meta}
                                                    noun={adminCopy.auditNoun}
                                                    busy={audit.isFetching}
                                                    onPage={(page) =>
                                                        setAuditFilters({ ...auditFilters, page })
                                                    }
                                                />
                                            }
                                        />
                                    ) : null}
                                </AdminRegion>
                            </div>

                            <div className="flex min-w-0 flex-col gap-[14px]">
                                <CreditRulesCard
                                    key={`${tenant.data.creditRules.blockOnEmpty}:${tenant.data.creditRules.graceCredits}`}
                                    tenantName={tenant.data.name}
                                    rules={tenant.data.creditRules}
                                    saving={creditRules.isPending}
                                    notice={
                                        creditRules.isSuccess ? adminCopy.creditRulesSaved : null
                                    }
                                    error={
                                        creditRules.error ? errorMessage(creditRules.error) : null
                                    }
                                    onSave={(rules) => creditRules.mutate(rules)}
                                />

                                <CreditBookingForm
                                    tenantName={tenant.data.name}
                                    actorName={currentUser()?.name ?? adminCopy.unknown}
                                    booking={book.isPending}
                                    result={book.data ?? null}
                                    error={book.error ? errorMessage(book.error) : null}
                                    onBook={(input) => book.mutate(input)}
                                />

                                <div id="meekijken" className="scroll-mt-4">
                                    <ImpersonationForm
                                        tenantUuid={tenant.data.uuid}
                                        tenantName={tenant.data.name}
                                        users={members.data?.items ?? []}
                                        initialUserUuid={searchParams.get(USER_PARAM) ?? ''}
                                        loadingUsers={members.isPending}
                                        starting={impersonate.isPending}
                                        error={
                                            impersonate.error
                                                ? errorMessage(impersonate.error)
                                                : null
                                        }
                                        onStart={({ user, reason, minutes }) =>
                                            impersonate.mutate(
                                                {
                                                    userUuid: user.uuid,
                                                    userName: user.name,
                                                    reason,
                                                    minutes,
                                                },
                                                {
                                                    /* Meekijken begint in de omgeving van de ander. */
                                                    onSuccess: () => void navigate(paths.dashboard),
                                                },
                                            )
                                        }
                                    />
                                </div>

                                <AdminRegion title={adminCopy.sessionsTitle} query={sessions}>
                                    {sessions.data ? (
                                        <ImpersonationSessionsCard
                                            sessions={sessions.data}
                                            ending={endSession.isPending}
                                            onEnd={(session) => endSession.mutate(session.uuid)}
                                        />
                                    ) : null}
                                </AdminRegion>
                            </div>
                        </div>
                    ) : null}
                </AdminRegion>
            </div>
        </div>
    );
}
